<script setup>
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { QSpinnerDots } from 'quasar'

import { usePlanningStore } from '@/stores/planning.store'

import { useNotify } from '@/composables/notify'

import { useQrcode } from '@/composables/qrcode'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import QrcodeScanner from '@/components/shared/QrcodeScanner.vue'
import EquipmentDetail from '@/components/shared/EquipmentDetail.vue'

const props = defineProps({
  allowedTagIds: {
    type: Array,
    required: false,
  },
  allowedUuids: {
    type: Array,
    required: false,
  },
  equipmentId: {
    type: Number,
    required: false,
  },
  continuous: {
    type: Boolean,
    default: false,
  },
  scanTotal: {
    type: Number,
    required: false,
    default: 0,
  },
  scanDone: {
    type: Number,
    required: false,
    default: 0,
  },
  scanNextName: {
    type: String,
    required: false,
    default: null,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const { getUUID } = useQrcode()
const { notifySuccess, notifyError, notifyWarning } = useNotify()
const equipment = ref(null)
const isScanning = ref(true)
const emit = defineEmits(['cancel', 'update', 'next'])

onMounted(async () => {
  if (props.continuous && !props.equipmentId) {
    isScanning.value = true
    return
  }
  equipment.value = (await planningStore.fetchEquipmentByIdAsync(props.equipmentId)).data
  isScanning.value = true
})

const onScanError = (error) => {
  console.error(error)
  notifyError(i18n.t('error.qrCodeNotFound'))
  isScanning.value = false
  emit('cancel')
}

const onDetect = async(codes) => {
  let shouldContinueScanning = false
  try {
    const decodedValue = codes[0].rawValue
    const checkNotificationDismiss = notifyWarning(`${i18n.t('common.qrCodeDetected')}! ${i18n.t('common.checking')}`, { spinner: QSpinnerDots })

    const uuid = getUUID(decodedValue)

    equipment.value = (await planningStore.fetchEquipmentByUuidAsync(uuid))?.data[0]
    checkNotificationDismiss()

    if ((props.allowedTagIds && props.allowedTagIds.length && !equipment.value.tags.some(tag => props.allowedTagIds.includes(tag))) || (props.allowedUuids && props.allowedUuids.length && !props.allowedUuids.includes(equipment.value.uuid))) {
      notifyWarning(i18n.t('warning.equipmentNotInList'))
      if (props.continuous) { equipment.value = null; shouldContinueScanning = true; return }
      emit('cancel')
      return
    }

    notifySuccess(i18n.t('common.equipmentFound'), { timeout: 500 })
    if (!equipment.value.condition) {
      equipment.value.condition = {
        condition: planningStore.equipmentConditionEnum.GOOD,
      }
    }
  } catch (error) {
    console.error(error)
    notifyError(i18n.t('error.qrCodeNotFound'))
    if (props.continuous) { shouldContinueScanning = true; return }
    emit('cancel')
    return
  } finally {
    isScanning.value = shouldContinueScanning
  }
}

const onConfirm = async() => {
  const scanned = equipment.value
  if (props.continuous) {
    equipment.value = null
    isScanning.value = true
    emit('next', scanned)
  } else {
    emit('update', scanned)
  }
}

</script>
<template>
  <q-card class="dialog-card-flex">
    <navigate-back
      :text="equipment?.name || i18n.t('common.scanEquipmentQrCode')"
      @cancel="emit('cancel')"
    >
      <template v-if="props.continuous && props.scanTotal > 0" #default>
        <div style="display: flex; flex-direction: column; justify-content: center;">
          <div class="text-h6">{{ equipment?.name || i18n.t('common.scanEquipmentQrCode') }}</div>
          <div class="text-caption text-grey-6">
            {{ props.scanDone + 1 }} / {{ props.scanTotal }}
            <span v-if="isScanning && props.scanNextName"> &mdash; {{ i18n.t('common.next') }}: <strong>{{ props.scanNextName }}</strong></span>
          </div>
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <qrcode-scanner
        v-if="isScanning"
        :timeout="50000"
        @detect="onDetect"
        @timeout="onScanError"
        @error="onScanError"
      />
      <div v-if="!isScanning">
        <equipment-detail
          hide-header
          :model-value="equipment"
          @cancel="emit('cancel')"
        />
      </div>
    </q-card-section>
    <q-card-actions v-if="!isScanning && equipment" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.confirm')"
        @click="onConfirm()"
      />
    </q-card-actions>
  </q-card>
</template>
